import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FileText, PhoneCall, CheckCircle2, Rocket, Download, Sparkles } from "lucide-react";
import ContactCTA from "./ContactCTA";

const steps = [
  {
    icon: FileText,
    title: "Apply Online — Free",
    desc: "Fill the 2-minute application form.",
  },
  {
    icon: PhoneCall,
    title: "Counselling Call",
    desc: "Our team helps you pick the right specialization.",
  },
  {
    icon: CheckCircle2,
    title: "Confirm Your Seat",
    desc: "Complete enrollment & secure your batch.",
  },
  {
    icon: Rocket,
    title: "Start Building — Day 1",
    desc: "Begin projects & AI training from Semester 1.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0 },
};

export default function SmruAdmissions() {
  return (
    <section id="admissions" className="py-20 sm:py-28 bg-gradient-to-b from-amber-50/50 via-white to-[#FFF5F0]/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        {/* Eyebrow + heading */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-aiorange/10 text-aiorange">
            <Sparkles className="w-3.5 h-3.5" />
            Admissions Open 2026–27
          </span>
          <h2 className="mt-5 text-3xl sm:text-5xl font-black tracking-tight text-gray-900">
            Your Seat in India's First AI B.Tech
          </h2>
          <p className="mt-4 text-base sm:text-lg text-gray-600">
            Four simple steps from <span className="text-aiblue font-semibold">AI.Prof</span>{" "}
        to a career-certain future.
          </p>
        </motion.div>

        {/* Stepper */}
        <div className="relative mt-14">
          {/* connecting line on lg */}
          <div className="hidden lg:block absolute top-7 left-0 right-0 mx-[12.5%] h-px bg-gradient-to-r from-aiorange/0 via-aiorange/30 to-aiorange/0" />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 relative">
            {steps.map((step, i) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.6, delay: i * 0.12 }}
                  className="relative text-center group flex flex-col"
                >
                  <div className="relative inline-flex items-center justify-center">
                    <div className="w-14 h-14 rounded-full bg-aiorange text-white font-display font-black text-xl flex items-center justify-center shadow-lg shadow-aiorange/30 ring-4 ring-white transition group-hover:-translate-y-1">
                      {i + 1}
                    </div>
                  </div>
                  <div className="mt-5 flex-1 bg-white rounded-2xl p-6 shadow-lg shadow-aiblue/10 transition hover:-translate-y-1 hover:shadow-xl hover:shadow-aiblue/15">
                    <Icon className="w-7 h-7 mx-auto text-aiblue" />
                    <h3 className="mt-3 text-lg font-extrabold tracking-tight text-gray-900">
                      {step.title}
                    </h3>
                    <p className="mt-2 text-sm text-gray-600 leading-relaxed">{step.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Big CTA card */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="relative mt-16 overflow-hidden rounded-[2.5rem] bg-gradient-to-br from-aiblue via-blue-600 to-aiorange p-10 sm:p-16 text-center text-white shadow-2xl shadow-aiblue/30"
        >
          {/* gradient accent strip */}
          <div className="absolute inset-x-0 top-0 h-1.5 gradient-motion" />
          {/* soft glow */}
          <div className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-[28rem] h-[28rem] rounded-full bg-aiblue/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 right-0 w-80 h-80 rounded-full bg-aiorange/15 blur-3xl" />

          <div className="relative">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-white/10 text-amber-200">
              <Sparkles className="h-3.5 w-3.5" />
              Your Journey Begins Here
            </div>

            <h3 className="mt-5 font-display font-black text-3xl sm:text-4xl tracking-tight">
              Your future in AI starts with one decision
            </h3>
            <p className="mt-3 text-base sm:text-lg text-white/80 max-w-2xl mx-auto">
              Don't just earn a degree — become the engineer the world is hiring
              for. Build real proof of your talent from Day 1 and graduate
              industry-ready with the AI.Prof ecosystem behind you.
            </p>

            {/* Buttons */}
            <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-4">
              <ContactCTA className="cta-pulse items-center justify-center gap-2 px-8 py-4 rounded-full bg-white text-aiblue font-extrabold text-base tracking-tight shadow-lg shadow-black/10 transition hover:-translate-y-0.5 hover:bg-gray-100" />
              <Link
                to="/RFC/brochure" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border-2 border-white/60 text-white font-extrabold text-base tracking-tight transition hover:bg-white hover:text-aiblue"
              >
                <Download className="w-5 h-5" />
                Download Brochure
              </Link>
            </div>
          </div>
        </motion.div>

        {/* Reassurance line */}
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.25 }}
          className="mt-6 text-center text-sm text-gray-500"
        >
          No application fee · Scholarships available · Mentorship from Day 1.
        </motion.p>
      </div>
    </section>
  );
}
